import {
  MutationFunction,
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query'

import { fetchOnClient } from '@/utils/fetcher/fetcher.client'

import { ConfigCacheOneDay } from '@/configs/constants'

import {
  DistrictResponse,
  DownloadStatisticDetailParams,
  FilterResponse,
  StatisticTableParams,
  StatisticTableResponse,
} from './types'
import {
  ENDPOINT,
  fetchGetBlob,
  getNextPageParam,
  getPreviousPageParam,
} from '../utils/fetcher/fetcher.helpers'

export const useFetchDistricts = (): UseQueryResult<
  DistrictResponse,
  Error
> => {
  return useQuery<DistrictResponse, Error>({
    queryKey: ['statistic-districts'],
    queryFn: async (): Promise<DistrictResponse> =>
      fetchOnClient(`${ENDPOINT.DISTRICT}`),
    ...ConfigCacheOneDay,
  })
}

export const useFetchMiscFilters = (): UseQueryResult<
  FilterResponse,
  Error
> => {
  return useQuery<FilterResponse, Error>({
    queryKey: ['statistic-filters'],
    queryFn: async (): Promise<FilterResponse> =>
      fetchOnClient(`${ENDPOINT.STATISTIC}/misc-filters`),
    ...ConfigCacheOneDay,
  })
}

export const useFetchStatisticTables = (
  params: StatisticTableParams,
  {
    enabled = false,
  }: {
    enabled: boolean
  }
) => {
  const { limit, keyword, educationLevelId } = params
  const baseQueryString = `limit=${limit}&educationLevelId=${educationLevelId}`
  const queryString = keyword
    ? `${baseQueryString}&keyword=${keyword}`
    : baseQueryString

  const queryKey = ['statistic-tables', params]
  const queryFn = async ({ pageParam }): Promise<StatisticTableResponse> => {
    return fetchOnClient(
      `${ENDPOINT.STATISTIC}/tables/search?${queryString}&offset=${
        pageParam || 0
      }`
    )
  }

  return useInfiniteQuery<StatisticTableResponse, Error>({
    queryKey,
    queryFn,
    getNextPageParam: getNextPageParam,
    getPreviousPageParam: getPreviousPageParam,
    enabled: enabled,
    initialPageParam: 0,
    ...ConfigCacheOneDay,
    gcTime: 0,
  })
}

export const useMutateDownloadStatisticDetail = (): UseMutationResult<
  Blob,
  Error,
  DownloadStatisticDetailParams
> => {
  const mutateFn: MutationFunction<Blob, DownloadStatisticDetailParams> = ({
    tableNum,
    districtId,
    districtLevel,
    educationLevelId,
    yearPeriod,
    educationStatus = 'EDUCATION_STATUS_UNSPECIFIED',
    format = 'csv',
  }: DownloadStatisticDetailParams): Promise<Blob> => {
    const queryString = `districtId=${districtId}&districtLevel=${districtLevel}&yearPeriod=${yearPeriod}&educationLevelId=${educationLevelId}&educationStatus=${educationStatus}${
      format ? '&format=' + format : ''
    }`

    // https://api.data.staging.belajar.id/data-portal-backend/v1/statistics/tables/101/download?format=xlsx

    return fetchGetBlob(
      `${ENDPOINT.STATISTIC}/tables/${tableNum}/download?${queryString}`
    )
  }

  return useMutation<Blob, Error, DownloadStatisticDetailParams>({
    mutationKey: ['statistic-download'],
    mutationFn: mutateFn,
  })
}
